import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const MAP_EMBED =
  "https://www.google.com/maps?q=3474+Mission+Street,+San+Francisco,+CA+94110&output=embed";
const DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=3474+Mission+Street,+San+Francisco,+CA+94110";

export default function Location() {
  const { t } = useLanguage();
  return (
    <section
      id="location"
      data-testid="location-section"
      className="relative section-pad bg-[#0a0a0a]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-12"
        >
          <span className="text-overline">{t("location.overline")}</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
            <span className="text-white">{t("location.title").split(" ")[0]} </span>
            <span className="gold-gradient-text italic">
              {t("location.title").split(" ").slice(1).join(" ")}
            </span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative overflow-hidden border border-[rgba(212,175,55,0.25)] rounded-sm"
            data-testid="location-map-wrapper"
          >
            <div className="aspect-[4/3] sm:aspect-[16/10] w-full bg-black">
              <iframe
                title="Lopez Appliances Map"
                src={MAP_EMBED}
                className="w-full h-full map-dark"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="pointer-events-none absolute inset-0 ring-1 ring-[rgba(212,175,55,0.15)]" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="card-surface p-8 md:p-10 h-full flex flex-col">
              <h3 className="font-serif-display text-2xl md:text-3xl text-white">
                Lopez Appliances
              </h3>
              <p className="mt-2 text-xs tracking-[0.25em] uppercase text-[#d4af37]">
                EST. 1985
              </p>
              <div className="gold-divider my-8" />

              <ul className="space-y-6 text-sm text-white/85">
                <li className="flex gap-4">
                  <MapPin size={18} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                  <a
                    href={DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-address"
                    className="hover:text-[#ffd700] transition-colors"
                  >
                    {t("location.address")}
                  </a>
                </li>
                <li className="flex gap-4">
                  <Phone size={18} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                  <a
                    href="tel:+14158268922"
                    data-testid="contact-phone"
                    className="hover:text-[#ffd700] transition-colors"
                  >
                    {t("location.phone")}
                  </a>
                </li>
                <li className="flex gap-4">
                  <Mail size={18} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                  <a
                    href="mailto:lopezappliance85@gmail.com"
                    data-testid="contact-email"
                    className="hover:text-[#ffd700] transition-colors break-all"
                  >
                    {t("location.email")}
                  </a>
                </li>
              </ul>

              <div className="mt-auto pt-10">
                <a
                  href={DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="get-directions-cta"
                  className="btn-gold w-full justify-center"
                >
                  {t("location.cta")}
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
